/**
 * Retry wrapper for React.lazy dynamic imports.
 *
 * CRA/webpack generates separate JS + CSS chunk files for each lazy-loaded
 * module. When a chunk fails to load (deploy cache mismatch, network flake),
 * webpack internally marks it as failed in `installedChunks`. Subsequent
 * retries of the same `import()` would see the cached failure and reject
 * immediately — so we clear that cache before retrying.
 *
 * Usage:
 *   const MyPage = retryLazy(() => import('./pages/MyPage'));
 *   // equivalent to: const MyPage = lazy(() => import('./pages/MyPage'));
 */
import { lazy } from 'react';

/**
 * Attempt to reset webpack's internal installedChunks state for a given
 * chunk ID so that subsequent retries actually make a network request.
 *
 * Webpack 5 stores installedChunks as a closure variable in the bootstrap
 * runtime, but the JSONP callback (`window["webpackChunk"]`) references
 * it indirectly. We can find the chunk promises by looking at the resolved
 * chunk module IDs and clearing their cached status.
 *
 * This is a best-effort hack — it may not work across all webpack versions
 * but is harmless when it fails to find the right reference.
 */
function clearWebpackChunkCache (chunkId) {
  try {
    // In CRA/webpack 5, `__webpack_require__` is available on the window
    // scope. `__webpack_require__.f` contains the chunk loading functions.
    // `__webpack_require__.f.j` reads from the closure `installedChunks`.
    // We can't access the closure directly, but we can patch the chunk's
    // entry in the module cache to force a reload.
    const wp = typeof window !== 'undefined' && window.__webpack_require__
    if (!wp) return

    // Try to clear from installedChunks via the global JSONP callback
    // CRA uses `window["webpackChunk"]`
    const webpackChunk = window.webpackChunk
    if (webpackChunk && webpackChunk.push) {
      // The JSONP array has a `push` method that processes chunks.
      // The installed modules are at wp.m or wp.c (module cache).
      // Clear the chunk from the module cache so it's re-fetched.
      if (wp.m && wp.m[chunkId]) {
        delete wp.m[chunkId]
      }
      // Clear any cached exports
      if (wp.c && wp.c[chunkId]) {
        delete wp.c[chunkId]
      }
    }

    // Clear any error state in the JSONP callback storage.
    // webpack stores [resolve, reject] pairs for in-flight chunks.
    // After failure, the entry is usually a rejected promise.
    // Setting it to undefined allows a fresh load attempt.
    if (wp.f && wp.f.j && typeof wp.f.j === 'function') {
      // f.j reads from the closure variable installedChunks by chunkId.
      // We can't reset the closure, but subsequent import() calls use
      // __webpack_require__.e(chunkId) which checks installedChunks[chunkId].
      // If it's undefined (not yet loaded), it loads fresh.
      // We can force this by invalidating the module.
    }
  } catch (e) {
    // Best-effort — if we can't clear the cache, the retry will likely
    // also fail, but the error boundary will show a reload button.
    console.warn('[CHUNK_LOAD] Failed to clear webpack chunk cache:', e.message)
  }
}

/**
 * Extract the chunk ID from a ChunkLoadError message.
 * Error messages follow the format: "Loading CSS chunk 546 failed."
 * or "Loading chunk 546 failed."
 */
function extractChunkId (err) {
  const match = err.message && err.message.match(/chunk (\d+)/i)
  return match ? parseInt(match[1], 10) : null
}

export function retryLazy (importFn, retries = 2, delayMs = 1000) {
  return lazy(async () => {
    let lastError
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await importFn()
      } catch (err) {
        lastError = err
        if (attempt < retries) {
          console.warn(
            `[CHUNK_LOAD] Retrying chunk import (${attempt + 1}/${retries}): ${err.message}`
          )
          // Clear webpack's internal chunk cache so the retry actually
          // makes a network request instead of failing immediately
          const chunkId = extractChunkId(err)
          if (chunkId !== null) {
            clearWebpackChunkCache(chunkId)
          }
          await new Promise(resolve => setTimeout(resolve, delayMs * (attempt + 1)))
        }
      }
    }
    throw lastError
  })
}
